import { downloadCSV } from "@/lib/downloadCSV";
import { getSchemaProperties } from "@/lib/dynamicSchemaFormValues";
import { PtsEmployeeService, SchemaService } from "@/services/APIs";

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const EXPORT_PAGE_SIZE = 10000;

/**
 * Primary export column order — matches the Add/Edit Employee form layout:
 * Personal Details first, then Employment Details (see form reference).
 */
const EXPORT_PRIMARY_COLUMNS: Array<{
  patterns: string[];
  titleHints: string[];
  /** Preferred API field name for this column (used first when present). */
  fieldName?: string;
  /** When true, field name must equal a pattern (avoids partial matches like "enrollment"). */
  exactName?: boolean;
}> = [
  {
    patterns: ["firstname", "first_name", "fname"],
    titleHints: ["first name", "firstname"],
  },
  {
    patterns: ["middlename", "middle_name", "mname"],
    titleHints: ["middle name", "middlename"],
  },
  {
    patterns: ["lastname", "last_name", "lname"],
    titleHints: ["last name", "lastname"],
  },
  {
    patterns: ["dateofbirth", "date_of_birth", "dob", "birthdate"],
    titleHints: ["date of birth", "birth date", "dob"],
  },
  {
    patterns: ["gender"],
    titleHints: ["gender"],
  },
  {
    fieldName: "phonenumber",
    patterns: ["phonenumber"],
    titleHints: ["phone number"],
    exactName: true,
  },
  {
    patterns: ["email", "emailaddress", "email_address"],
    titleHints: ["email address", "email"],
  },
  {
    patterns: [
      "joiningdate",
      "joining_date",
      "dateofjoin",
      "date_of_join",
      "join_date",
      "doj",
    ],
    titleHints: [
      "date of join",
      "joining date",
      "join date",
      "date of joining",
    ],
  },
  {
    patterns: ["designation", "designation_id"],
    titleHints: ["designation"],
  },
  {
    patterns: ["department", "department_id"],
    titleHints: ["department"],
  },
  {
    patterns: [
      "reportingmanager",
      "reporting_manager",
      "reportingmanager_id",
      "reporting_to",
    ],
    titleHints: ["reporting manager", "reporting to"],
  },
  {
    patterns: ["grade"],
    titleHints: ["grade"],
  },
  {
    patterns: ["personalid", "personal_id", "personnelid"],
    titleHints: ["personal id", "personnel id"],
  },
  {
    patterns: ["role"],
    titleHints: ["role"],
    exactName: true,
  },
  {
    patterns: [
      "serviceenddate",
      "service_end_date",
      "enddate",
      "employmentenddate",
    ],
    titleHints: ["service end date", "end date", "employment end"],
  },
];

export type DropdownOption = { value: string; label: string };

export type ExportEmployeeField = {
  fieldName: string;
  title: string;
};

export type ExportEmployeesParams = {
  /** API filters (search, ordering, etc.) */
  filters?: Record<string, unknown>;
  /** When set, only these employee IDs are exported */
  selectedIds?: string[];
  /** Used in the downloaded filename */
  searchQuery?: string;
  designationOptions?: DropdownOption[];
  departmentOptions?: DropdownOption[];
  columnGridData?: {
    grid?: Array<{ field_name: string; field_title?: string }>;
  };
  schemaInfo?: unknown;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "-";
  }
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "-";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return "-";
  }
};

export const escapeCsvValue = (value: string): string => {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const getEmployeeRecordId = (
  record: Record<string, unknown>
): string => {
  const recordId = record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";
  return recordId ? String(recordId) : "";
};

const humanizeFieldName = (fieldName: string): string =>
  fieldName
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase());

const getFieldValue = (
  record: Record<string, unknown>,
  fieldName: string
): unknown => {
  const lower = fieldName.toLowerCase();
  return (
    record?.[fieldName] ??
    record?.[lower] ??
    record?.[fieldName.toUpperCase()] ??
    ""
  );
};

const getSchemaFieldTitle = (
  fieldName: string,
  schemaProps: Record<string, { title?: string }>
): string | undefined => {
  const prop =
    schemaProps[fieldName] ??
    schemaProps[fieldName.toLowerCase()] ??
    schemaProps[fieldName.toUpperCase()];
  return typeof prop?.title === "string" ? prop.title : undefined;
};

const normalizeTitle = (title: string): string =>
  title.toLowerCase().replace(/\s+/g, " ").trim();

const fieldMatchesPrimarySlot = (
  fieldName: string,
  title: string,
  slot: (typeof EXPORT_PRIMARY_COLUMNS)[number]
): boolean => {
  const lowerName = fieldName.toLowerCase();
  const nameMatches = slot.patterns.some(pattern =>
    slot.exactName ? lowerName === pattern : lowerName.includes(pattern)
  );
  if (nameMatches) {
    return true;
  }
  const normalizedTitle = normalizeTitle(title);
  return slot.titleHints.some(hint => normalizedTitle.includes(hint));
};

/** Resolve form-order primary columns from available field names. */
const resolvePrimaryFieldOrder = (
  fieldNames: Set<string>,
  gridTitleByField: Map<string, string>,
  schemaProps: Record<string, { title?: string }>
): string[] => {
  const used = new Set<string>();
  const ordered: string[] = [];

  for (const slot of EXPORT_PRIMARY_COLUMNS) {
    const preferredName = slot.fieldName?.toLowerCase();
    if (
      preferredName &&
      fieldNames.has(preferredName) &&
      !used.has(preferredName)
    ) {
      used.add(preferredName);
      ordered.push(preferredName);
      continue;
    }

    const match = Array.from(fieldNames).find(candidate => {
      if (used.has(candidate)) {
        return false;
      }
      const title =
        gridTitleByField.get(candidate) ??
        getSchemaFieldTitle(candidate, schemaProps) ??
        humanizeFieldName(candidate);
      return fieldMatchesPrimarySlot(candidate, title, slot);
    });

    if (match) {
      used.add(match);
      ordered.push(match);
    }
  }

  return ordered;
};

const sortRemainingFields = (
  a: string,
  b: string,
  schemaProps: Record<string, { title?: string }>
): number => {
  const schemaOrder = Object.keys(schemaProps).map(k => k.toLowerCase());
  const indexA = schemaOrder.indexOf(a);
  const indexB = schemaOrder.indexOf(b);
  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }
  if (indexA !== -1) {
    return -1;
  }
  if (indexB !== -1) {
    return 1;
  }
  return a.localeCompare(b);
};

/**
 * Build the full export column set from schema properties, grid metadata, and record keys.
 * Form fields (personal + employment) are placed first, then remaining fields.
 */
export const resolveExportFields = (
  schemaInfo: unknown,
  columnGridData: ExportEmployeesParams["columnGridData"],
  records: Record<string, unknown>[]
): ExportEmployeeField[] => {
  const schemaProps = getSchemaProperties(schemaInfo);
  const gridTitleByField = new Map<string, string>();

  columnGridData?.grid?.forEach(field => {
    const key = field.field_name.toLowerCase();
    if (field.field_title) {
      gridTitleByField.set(key, field.field_title);
    }
  });

  const fieldNames = new Set<string>();

  Object.keys(schemaProps).forEach(key => {
    fieldNames.add(key.toLowerCase());
  });

  records.forEach(record => {
    Object.keys(record).forEach(key => {
      if (key && typeof key === "string") {
        fieldNames.add(key.toLowerCase());
      }
    });
  });

  const primaryKeys = resolvePrimaryFieldOrder(
    fieldNames,
    gridTitleByField,
    schemaProps
  );
  const primarySet = new Set(primaryKeys);

  const remainingKeys = Array.from(fieldNames)
    .filter(name => !primarySet.has(name))
    .sort((a, b) => sortRemainingFields(a, b, schemaProps));

  const orderedKeys = [...primaryKeys, ...remainingKeys];

  return orderedKeys.map(fieldName => ({
    fieldName,
    title:
      gridTitleByField.get(fieldName) ??
      getSchemaFieldTitle(fieldName, schemaProps) ??
      humanizeFieldName(fieldName),
  }));
};

const formatExportCellValue = (
  field: ExportEmployeeField,
  record: Record<string, unknown>,
  designationOptions: DropdownOption[],
  departmentOptions: DropdownOption[]
): string => {
  const fieldName = field.fieldName.toLowerCase();
  const fieldTitle = field.title.toLowerCase();
  const rawValue = getFieldValue(record, fieldName);

  const isDesignationField =
    fieldName.includes("designation") || fieldTitle.includes("designation");
  const isDepartmentField =
    fieldName.includes("department") || fieldTitle.includes("department");
  const isDateField =
    fieldName.includes("dateofbirth") ||
    fieldName.includes("date_of_birth") ||
    fieldName.includes("dob") ||
    fieldName.includes("date") ||
    fieldTitle.includes("date") ||
    fieldTitle.includes("birth");
  const isGenderField =
    fieldName.includes("gender") || fieldTitle.includes("gender");
  const isActiveField = fieldName === "is_active";

  if (isActiveField) {
    const isActive =
      record?.is_active === true ||
      record?.is_active === "true" ||
      record?.Is_Active === true;
    return isActive ? "Yes" : "No";
  }

  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "-";
  }

  if (typeof rawValue === "boolean") {
    return rawValue ? "Yes" : "No";
  }

  if (
    Array.isArray(rawValue) ||
    (typeof rawValue === "object" && rawValue !== null)
  ) {
    try {
      return JSON.stringify(rawValue);
    } catch {
      return String(rawValue);
    }
  }

  if (isDesignationField) {
    const designation = designationOptions.find(
      item => item.value === String(rawValue)
    );
    return designation?.label ?? String(rawValue);
  }

  if (isDepartmentField) {
    const department = departmentOptions.find(
      item => item.value === String(rawValue)
    );
    return department?.label ?? String(rawValue);
  }

  if (isDateField && typeof rawValue === "string") {
    return formatDate(rawValue);
  }

  if (isGenderField) {
    const genderString = String(rawValue);
    return (
      genderString.charAt(0).toUpperCase() + genderString.slice(1).toLowerCase()
    );
  }

  return String(rawValue);
};

export const buildEmployeeCsv = (
  records: Record<string, unknown>[],
  exportFields: ExportEmployeeField[],
  designationOptions: DropdownOption[] = [],
  departmentOptions: DropdownOption[] = []
): string => {
  if (!exportFields.length) {
    return "";
  }

  const headers = exportFields.map(field => field.title);
  const rows = records.map(record =>
    exportFields
      .map(field =>
        escapeCsvValue(
          formatExportCellValue(
            field,
            record,
            designationOptions,
            departmentOptions
          )
        )
      )
      .join(",")
  );

  return [headers.join(","), ...rows].join("\n");
};

/** Fetch every employee page for the given filters. */
export const fetchAllEmployeeRecords = async (
  filters: Record<string, unknown> = {}
): Promise<Record<string, unknown>[]> => {
  const response = await PtsEmployeeService.getEmployees(
    filters,
    1,
    EXPORT_PAGE_SIZE
  );

  let allRecords: Record<string, unknown>[] = [];
  if (Array.isArray(response)) {
    allRecords = response;
  } else if (response?.results && Array.isArray(response.results)) {
    allRecords = response.results;
    if (response.count > allRecords.length) {
      const totalPages = Math.ceil(response.count / EXPORT_PAGE_SIZE);
      for (let page = 2; page <= totalPages; page++) {
        const pageResponse = await PtsEmployeeService.getEmployees(
          filters,
          page,
          EXPORT_PAGE_SIZE
        );
        if (pageResponse?.results && Array.isArray(pageResponse.results)) {
          allRecords = [...allRecords, ...pageResponse.results];
        }
      }
    }
  }

  return allRecords;
};

const buildExportFilename = (
  searchQuery: string | undefined,
  selectedIds: string[] | undefined
): string => {
  const dateStamp = new Date().toISOString().split("T")[0];
  if (selectedIds?.length) {
    return `employees_selected_${dateStamp}`;
  }
  if (searchQuery?.trim()) {
    return `employees_${searchQuery.trim()}_${dateStamp}`;
  }
  return `employees_all_${dateStamp}`;
};

/**
 * Export employees to CSV with every schema field (plus any extra API fields on records).
 */
export const exportEmployeesToCsv = async (
  params: ExportEmployeesParams
): Promise<{ count: number; filename: string }> => {
  const {
    filters = { ordering: "firstname" },
    selectedIds,
    searchQuery,
    designationOptions = [],
    departmentOptions = [],
    columnGridData,
  } = params;

  const schemaInfo =
    params.schemaInfo ??
    (await SchemaService.getSchemaByName(EMPLOYEE_SCHEMA_NAME));

  let records = await fetchAllEmployeeRecords(filters);

  if (selectedIds?.length) {
    const selectedSet = new Set(selectedIds.map(id => String(id)));
    records = records.filter(record =>
      selectedSet.has(getEmployeeRecordId(record))
    );
  }

  if (records.length === 0) {
    throw new Error(
      selectedIds?.length
        ? "No selected employees found to export"
        : "No employee records found to export"
    );
  }

  const exportFields = resolveExportFields(schemaInfo, columnGridData, records);
  if (!exportFields.length) {
    throw new Error("No employee fields available for export");
  }

  const csvContent = buildEmployeeCsv(
    records,
    exportFields,
    designationOptions,
    departmentOptions
  );

  const filename = buildExportFilename(searchQuery, selectedIds);
  downloadCSV(csvContent, filename);

  return { count: records.length, filename };
};
